import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import type { DpEmployeeDetail } from "../api/employeesApi";
import {
  ACCRUAL_STATUS_BADGE,
  ACCRUAL_STATUS_LABELS,
  cancelVacationScheduleApi,
  DpAccrualPeriod,
  DpVacationSchedule,
  getVacationNoticeBlobApi,
  hasVacationPayment,
  issueVacationNoticeApi,
  listEmployeeVacationsApi,
  scheduleVacationApi,
  updateVacationEntitlementApi,
  uploadVacationFileApi,
  VACATION_STATUS_BADGE,
  VACATION_STATUS_LABELS,
  vacationPaymentBadge,
} from "../api/vacationsApi";
import { DOCUMENT_FILE_MAX_BYTES, DOCUMENT_FILE_TYPES } from "../api/documentsApi";
import { getEmployeeTimekeepingSummaryApi } from "../api/timekeepingApi";
import { formatDateOnly } from "../utils/format";
import DocumentFileButton from "./DocumentFileButton";
import ReasonModal from "./ReasonModal";
import RuleMessages from "./RuleMessages";
import VacationPaymentModal from "./VacationPaymentModal";

type Props = {
  token: string;
  employee: DpEmployeeDetail;
  /** Programar/cancelar muda o status do colaborador (Férias) — a ficha recarrega o cabeçalho. */
  onChanged: () => void;
};

/** 'YYYY-MM-DD' + N dias, sem passar pelo fuso local. */
const addDaysIso = (iso: string, days: number) => {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const daysInclusive = (start: string, end: string) =>
  Math.round((new Date(`${end}T00:00:00Z`).getTime() - new Date(`${start}T00:00:00Z`).getTime()) / 86_400_000) + 1;

/** O backend devolve a lista de regras como mensagem única separada por " | ". */
const splitRules = (message?: string) => String(message ?? "").split(" | ").filter(Boolean);

export const canSchedule = (period: DpAccrualPeriod) =>
  period.balance > 0 && ["DISPONIVEL", "PARCIALMENTE_GOZADO", "VENCIDO"].includes(period.status);

/** Aba "Férias": períodos aquisitivos (gerados pela admissão), saldo e programações. */
export default function EmployeeVacationsTab({ token, employee, onChanged }: Props) {
  const [periods, setPeriods] = useState<DpAccrualPeriod[]>([]);
  const [loading, setLoading] = useState(false);
  const [scheduling, setScheduling] = useState<DpAccrualPeriod | null>(null);
  const [entitlement, setEntitlement] = useState<DpAccrualPeriod | null>(null);
  const [notice, setNotice] = useState<DpVacationSchedule | null>(null);
  const [canceling, setCanceling] = useState<DpVacationSchedule | null>(null);
  const [receipt, setReceipt] = useState<DpVacationSchedule | null>(null);
  /** Faltas vindas dos espelhos de ponto fechados, por período aquisitivo (null = ponto desligado). */
  const [fromPonto, setFromPonto] = useState<Map<string, number> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPeriods(await listEmployeeVacationsApi(token, employee.id));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar as férias.");
    } finally {
      setLoading(false);
    }
  }, [token, employee.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getEmployeeTimekeepingSummaryApi(token, employee.id)
      .then((s) => setFromPonto(s.enabled ? new Map(s.vacationPeriods.map((v) => [v.id, v.absencesFromPonto])) : null))
      .catch(() => setFromPonto(null));
  }, [token, employee.id, periods]);

  const changed = () => {
    load();
    onChanged();
  };

  const multipleContracts = employee.contracts.length > 1;
  const admissionOf = (contractId: string) =>
    employee.contracts.find((c) => c.id === contractId)?.admissionDate ?? null;

  return (
    <section className="dpSection">
      <div className="dpHeader" style={{ marginBottom: 12 }}>
        <h2 className="dpSection__title" style={{ margin: 0 }}>
          Férias
        </h2>
      </div>

      <p className="dpFormHint" style={{ marginTop: 0 }}>
        Os períodos aquisitivos são gerados a partir da admissão. Faltas injustificadas reduzem o direito
        (art. 130 CLT). Com o ponto eletrônico ligado, o fechamento do espelho atualiza as faltas do período; antes
        dele, são informadas manualmente.
      </p>

      {loading ? (
        <div className="dpEmpty">Carregando...</div>
      ) : periods.length === 0 ? (
        <div className="dpEmpty">Sem contrato: não há período aquisitivo.</div>
      ) : (
        <div className="dpTimeline">
          {periods.map((period) => (
            <article
              key={period.id}
              className={`dpTimelineItem ${canSchedule(period) ? "is-current" : ""}`}
            >
              <div className="dpTimelineItem__header">
                <div>
                  <div className="dpTimelineItem__period">
                    {period.sequence}º período · {formatDateOnly(period.acquisitionStart)} a{" "}
                    {formatDateOnly(period.acquisitionEnd)}{" "}
                    <span className={`dpBadge ${ACCRUAL_STATUS_BADGE[period.status]}`}>
                      {ACCRUAL_STATUS_LABELS[period.status]}
                    </span>
                  </div>
                  {multipleContracts ? (
                    <div className="dpMuted">Contrato com admissão em {formatDateOnly(admissionOf(period.contractId))}</div>
                  ) : null}
                </div>
                <div className="dpPeriodActions">
                  {period.status !== "EM_AQUISICAO" ? (
                    <button
                      className="dpBtn dpBtn--secondary dpBtn--small"
                      onClick={() => setEntitlement(period)}
                    >
                      Faltas do período
                    </button>
                  ) : null}
                  {canSchedule(period) ? (
                    <button className="dpBtn dpBtn--primary dpBtn--small" onClick={() => setScheduling(period)}>
                      Programar férias
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="dpDefinitionGrid">
                <Field label="Direito" value={`${period.entitledDays} dias`} />
                <Field
                  label="Faltas injustificadas"
                  value={
                    fromPonto?.has(period.id) ? (
                      <>
                        {period.unjustifiedAbsences} <span className="dpMuted">· faltas vindas do ponto: {fromPonto.get(period.id)}</span>
                      </>
                    ) : (
                      String(period.unjustifiedAbsences)
                    )
                  }
                />
                <Field label="Saldo" value={<strong>{period.balance} dias</strong>} />
                <Field label="Conceder até" value={formatDateOnly(period.concessionEnd)} />
              </div>

              {period.lostByLeave ? (
                <p className="dpNotice" style={{ marginTop: 12 }}>
                  Direito perdido: mais de 6 meses de benefício do INSS dentro do período aquisitivo (art. 133 IV).
                  {period.restartsOn
                    ? ` Nova contagem a partir de ${formatDateOnly(period.restartsOn)} (art. 133 §2).`
                    : " A nova contagem começa no retorno do afastamento (art. 133 §2)."}
                </p>
              ) : null}
              {period.alerts.length > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <RuleMessages warnings={period.alerts} />
                </div>
              ) : null}

              {period.schedules.length > 0 ? (
                <div className="dpTableWrapper" style={{ marginTop: 12 }}>
                  <table className="dpTable">
                    <thead>
                      <tr>
                        <th>Gozo</th>
                        <th>Dias</th>
                        <th>Abono</th>
                        <th>Aviso</th>
                        <th>Situação</th>
                        <th style={{ width: 280 }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {period.schedules.map((s) => {
                        const open = s.status === "PLANEJADO" || s.status === "AVISADO";
                        return (
                          <tr key={s.id} style={s.status === "CANCELADO" ? { opacity: 0.6 } : undefined}>
                            <td>
                              {formatDateOnly(s.startDate)} a {formatDateOnly(s.endDate)}
                              {s.thirteenthAdvance ? <div className="dpMuted">com adiantamento do 13º</div> : null}
                              {s.cancelReason ? <div className="dpMuted">Cancelada: {s.cancelReason}</div> : null}
                            </td>
                            <td>{s.days}</td>
                            <td>{s.abonoDays > 0 ? `${s.abonoDays} dias` : "—"}</td>
                            <td>
                              {s.noticeIssuedAt ? (
                                formatDateOnly(s.noticeIssuedAt)
                              ) : s.status === "CANCELADO" ? (
                                "—"
                              ) : (
                                <span className="dpMuted">até {formatDateOnly(s.noticeDeadline)}</span>
                              )}
                            </td>
                            <td>
                              <span className={`dpBadge ${VACATION_STATUS_BADGE[s.status]}`}>
                                {VACATION_STATUS_LABELS[s.status]}
                              </span>
                              {hasVacationPayment(s) ? (
                                <div style={{ marginTop: 4 }}>
                                  <span className={`dpBadge ${vacationPaymentBadge(s.payment).className}`}>
                                    Recibo: {vacationPaymentBadge(s.payment).label}
                                  </span>
                                </div>
                              ) : null}
                            </td>
                            <td>
                              <div className="dpTable__actions">
                                {s.hasNoticeFile ? (
                                  <DocumentFileButton
                                    token={token}
                                    fetchBlob={() => getVacationNoticeBlobApi(token, s.id)}
                                    fileName={s.noticeFileName}
                                    label="Aviso"
                                  />
                                ) : null}
                                {hasVacationPayment(s) ? (
                                  <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => setReceipt(s)}>
                                    Recibo
                                  </button>
                                ) : null}
                                {open ? (
                                  <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => setNotice(s)}>
                                    {s.noticeIssuedAt ? "Corrigir aviso" : "Registrar aviso"}
                                  </button>
                                ) : null}
                                {open && s.daysToStart > 0 ? (
                                  <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => setCanceling(s)}>
                                    Cancelar
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}

      {scheduling ? (
        <ScheduleModal
          token={token}
          period={scheduling}
          onClose={() => setScheduling(null)}
          onSaved={() => {
            setScheduling(null);
            changed();
          }}
        />
      ) : null}

      {entitlement ? (
        <EntitlementModal
          token={token}
          period={entitlement}
          onClose={() => setEntitlement(null)}
          onSaved={() => {
            setEntitlement(null);
            load();
          }}
        />
      ) : null}

      {notice ? (
        <NoticeModal
          token={token}
          schedule={notice}
          onClose={() => setNotice(null)}
          onSaved={() => {
            setNotice(null);
            changed();
          }}
        />
      ) : null}

      {receipt ? (
        <VacationPaymentModal token={token} schedule={receipt} onClose={() => setReceipt(null)} onChanged={load} />
      ) : null}

      {canceling ? (
        <ReasonModal
          title="Cancelar férias"
          description={`Férias de ${formatDateOnly(canceling.startDate)} a ${formatDateOnly(canceling.endDate)}. Os dias voltam ao saldo.`}
          confirmLabel="Cancelar férias"
          onClose={() => setCanceling(null)}
          onConfirm={async (reason) => {
            const result = await cancelVacationScheduleApi(token, canceling.id, reason);
            toast.success("Férias canceladas.");
            for (const w of result.financeiroWarnings ?? []) toast.warning(w, { autoClose: false });
            setCanceling(null);
            changed();
          }}
        />
      ) : null}
    </section>
  );
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="dpDefinition__label">{label}</div>
    <div className="dpDefinition__value">{value}</div>
  </div>
);

// ── Modais ──────────────────────────────────────────────────────────────────

export function ScheduleModal({
  token,
  period,
  onClose,
  onSaved,
}: {
  token: string;
  period: DpAccrualPeriod;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [startDate, setStartDate] = useState("");
  const [days, setDays] = useState(String(Math.min(period.balance, 30)));
  const [abonoDays, setAbonoDays] = useState("0");
  const [thirteenthAdvance, setThirteenthAdvance] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const daysNum = Number(days) || 0;
  const abonoNum = Number(abonoDays) || 0;
  const endDate = startDate && daysNum > 0 ? addDaysIso(startDate, daysNum - 1) : "";

  const submit = async () => {
    if (!startDate) return toast.error("Informe o início.");
    if (daysNum < 1) return toast.error("Informe a quantidade de dias.");
    setSaving(true);
    setErrors([]);
    try {
      const { warnings } = await scheduleVacationApi(token, period.id, {
        startDate,
        endDate,
        abonoDays: abonoNum,
        thirteenthAdvance,
      });
      toast.success("Férias programadas.");
      warnings.forEach((warning) => toast.warning(warning));
      onSaved();
    } catch (error: any) {
      setErrors(splitRules(error?.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal">
        <header className="dpModal__header">
          <h2 className="dpModal__title">Programar férias — {period.sequence}º período</h2>
          <button className="dpModal__close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </header>

        <div className="dpModal__body">
          <p className="dpNotice">
            Saldo de <strong>{period.balance} dias</strong>; conceder até {formatDateOnly(period.concessionEnd)}.
            Pode dividir em até 3 períodos: um com pelo menos 14 dias e nenhum com menos de 5.
          </p>

          <div className="dpFormGrid">
            <div className="dpFormGroup">
              <label htmlFor="vacStart">Início *</label>
              <input
                id="vacStart"
                type="date"
                className="dpInput"
                value={startDate}
                disabled={saving}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="dpFormGroup">
              <label htmlFor="vacDays">Dias de gozo *</label>
              <input
                id="vacDays"
                type="number"
                min={1}
                max={30}
                className="dpInput"
                value={days}
                disabled={saving}
                onChange={(e) => setDays(e.target.value)}
              />
              {endDate ? <span className="dpFormHint">Último dia: {formatDateOnly(endDate)}</span> : null}
            </div>
            <div className="dpFormGroup">
              <label htmlFor="vacAbono">Abono pecuniário (dias vendidos)</label>
              <input
                id="vacAbono"
                type="number"
                min={0}
                max={period.maxAbonoDays}
                className="dpInput"
                value={abonoDays}
                disabled={saving || period.maxAbonoDays === 0}
                onChange={(e) => setAbonoDays(e.target.value)}
              />
              <span className="dpFormHint">
                {period.maxAbonoDays === 0
                  ? "O abono deste período já foi lançado."
                  : `Até ${period.maxAbonoDays} dias (1/3 do direito), uma vez por período.`}
              </span>
            </div>
            <div className="dpFormGroup">
              <label className="dpCheckbox" style={{ marginTop: 28 }}>
                <input
                  type="checkbox"
                  checked={thirteenthAdvance}
                  disabled={saving}
                  onChange={(e) => setThirteenthAdvance(e.target.checked)}
                />
                Adiantar a 1ª parcela do 13º
              </label>
            </div>
          </div>

          {startDate && daysNum > 0 ? (
            <p className="dpFormHint">
              Usa {daysNum + abonoNum} dia(s) do saldo; aviso ao colaborador até{" "}
              {formatDateOnly(addDaysIso(startDate, -30))}.
            </p>
          ) : null}

          <RuleMessages errors={errors} />
        </div>

        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="dpBtn dpBtn--primary" onClick={submit} disabled={saving}>
            {saving ? "Salvando..." : "Programar"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function EntitlementModal({
  token,
  period,
  onClose,
  onSaved,
}: {
  token: string;
  period: DpAccrualPeriod;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [absences, setAbsences] = useState(String(period.unjustifiedAbsences));
  const [notes, setNotes] = useState(period.notes ?? "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const value = Number(absences);
    if (!Number.isInteger(value) || value < 0) return toast.error("Informe um número de faltas válido.");
    setSaving(true);
    try {
      const saved = await updateVacationEntitlementApi(token, period.id, { unjustifiedAbsences: value, notes: notes.trim() || null });
      toast.success(`Direito do período: ${saved.entitledDays} dias.`);
      onSaved();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal">
        <header className="dpModal__header">
          <h2 className="dpModal__title">Faltas injustificadas — {period.sequence}º período</h2>
          <button className="dpModal__close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </header>
        <div className="dpModal__body">
          <p className="dpNotice">
            Art. 130 CLT: até 5 faltas = 30 dias; 6 a 14 = 24; 15 a 23 = 18; 24 a 32 = 12; mais de 32 = sem
            direito. Informado manualmente até o ponto eletrônico existir.
          </p>
          <div className="dpFormGroup">
            <label htmlFor="absences">Faltas injustificadas no período aquisitivo</label>
            <input
              id="absences"
              type="number"
              min={0}
              className="dpInput"
              value={absences}
              disabled={saving}
              onChange={(e) => setAbsences(e.target.value)}
            />
          </div>
          <div className="dpFormGroup">
            <label htmlFor="absNotes">Observação</label>
            <textarea
              id="absNotes"
              className="dpTextarea"
              maxLength={500}
              value={notes}
              disabled={saving}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="dpBtn dpBtn--primary" onClick={submit} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function NoticeModal({
  token,
  schedule,
  onClose,
  onSaved,
}: {
  token: string;
  schedule: DpVacationSchedule;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [issuedAt, setIssuedAt] = useState(schedule.noticeIssuedAt?.slice(0, 10) ?? new Date().toLocaleDateString("en-CA"));
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const handleFile = (selected?: File) => {
    if (!selected) return setFile(null);
    if (!DOCUMENT_FILE_TYPES.includes(selected.type)) return toast.error("Envie PDF, JPG, PNG ou WEBP.");
    if (selected.size > DOCUMENT_FILE_MAX_BYTES) return toast.error("O arquivo pode ter no máximo 10 MB.");
    setFile(selected);
  };

  const submit = async () => {
    if (!issuedAt) return toast.error("Informe a data do aviso.");
    setSaving(true);
    try {
      const uploaded = file ? await uploadVacationFileApi(token, file) : null;
      const { warnings } = await issueVacationNoticeApi(token, schedule.id, {
        noticeIssuedAt: issuedAt,
        ...(uploaded ? { noticeAssetId: uploaded.assetId, noticeFileName: uploaded.fileName ?? file?.name ?? null } : {}),
      });
      toast.success("Aviso registrado.");
      warnings.forEach((warning) => toast.warning(warning));
      onSaved();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao registrar o aviso.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal">
        <header className="dpModal__header">
          <h2 className="dpModal__title">Aviso de férias</h2>
          <button className="dpModal__close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </header>
        <div className="dpModal__body">
          <p className="dpNotice">
            Férias de {formatDateOnly(schedule.startDate)} a {formatDateOnly(schedule.endDate)} (
            {daysInclusive(schedule.startDate.slice(0, 10), schedule.endDate.slice(0, 10))} dias). O aviso deve ser dado
            até {formatDateOnly(schedule.noticeDeadline)} (art. 135 CLT). Com o aviso registrado, o colaborador
            passa a constar em férias nos dias de gozo.
          </p>
          <div className="dpFormGroup">
            <label htmlFor="noticeDate">Data do aviso *</label>
            <input
              id="noticeDate"
              type="date"
              className="dpInput"
              value={issuedAt}
              disabled={saving}
              onChange={(e) => setIssuedAt(e.target.value)}
            />
          </div>
          <div className="dpFormGroup">
            <label htmlFor="noticeFile">Aviso assinado (PDF ou imagem, até 10 MB)</label>
            <input
              id="noticeFile"
              type="file"
              className="dpInput"
              accept={DOCUMENT_FILE_TYPES.join(",")}
              disabled={saving}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <span className="dpFormHint">
              {file ? file.name : schedule.hasNoticeFile ? "Já há um aviso anexado; enviar outro substitui." : "Opcional."}
            </span>
          </div>
        </div>
        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="dpBtn dpBtn--primary" onClick={submit} disabled={saving}>
            {saving ? "Salvando..." : "Registrar aviso"}
          </button>
        </footer>
      </div>
    </div>
  );
}
